'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface ForgotPasswordData {
  email: string;
}

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export default function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<ForgotPasswordData>({
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordData) => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      if (error) throw error;
      setSent(true);
      toast.success('Reset link sent to ' + data.email);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center space-y-5">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
          <CheckCircle2 size={32} className="text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Check your email</h2>
          <p className="text-muted-foreground text-sm mt-2">
            We sent a password reset link to{' '}
            <span className="font-semibold text-foreground">{getValues('email')}</span>
          </p>
          <p className="text-muted-foreground text-xs mt-2">
            Didn&apos;t receive it? Check your spam folder or try again in a few minutes.
          </p>
        </div>
        <button onClick={onBack} className="btn-primary w-full justify-center py-3">
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        <ArrowLeft size={16} />
        Back to Sign In
      </button>

      <div>
        <h2 className="text-2xl font-bold text-foreground">Reset your password</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </p>
      </div>

      <div>
        <label htmlFor="forgot-email" className="block text-sm font-semibold text-foreground mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            id="forgot-email"
            type="email"
            placeholder="you@company.com"
            className={`input-field pl-9 ${errors.email ? 'border-red-500' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
        </div>
        {errors.email && <p className="text-red-500 text-xs mt-1.5">⚠ {errors.email.message}</p>}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 text-base"
      >
        {isLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Sending reset link...
          </>
        ) : (
          'Send Reset Link'
        )}
      </button>
    </form>
  );
}